import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, Calendar, User, Share2 } from 'lucide-react';
import { blogPostsData } from '../data/blogPostsData';
import InquiryModal from '../components/InquiryModal';

const BlogDetail = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');
  const handleButtonClick = (context) => { setButtonContext(context); setIsModalOpen(true); };
  
  const post = blogPostsData.find(p => p.id === parseInt(id));
  
  if (!post) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-[#111C16] mb-4">Article Not Found</h1>
          <p className="font-body text-[#4A5D53] mb-8">The article you are looking for does not exist.</p>
          <Link to="/blogs"><Button className="bg-[#1A3628] hover:bg-[#11241A] text-white"><ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} />Back to Articles</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-white">
      {/* Hero Image */}
      <div className="relative h-80 md:h-96 overflow-hidden" data-testid="blog-detail-hero">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111C16]/90 via-[#111C16]/40 to-transparent"></div>
        
        <div className="absolute top-8 left-6 md:left-12 z-10">
          <Link to="/blogs">
            <Button className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20" data-testid="blog-back-btn">
              <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} />Back to Articles
            </Button>
          </Link>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-block px-3 py-1 bg-[#1A3628] text-white text-xs font-body font-semibold uppercase tracking-wider">{post.category}</span>
              {post.featured && <span className="inline-block px-3 py-1 bg-[#C85A40] text-[#111C16] text-xs font-body font-semibold uppercase tracking-wider">Featured</span>}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight leading-tight">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C85A40] flex items-center justify-center">
                  <User className="h-5 w-5 text-[#111C16]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-body text-sm font-semibold text-white">{post.author}</p>
                  <p className="font-body text-xs text-white/60">{post.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 font-body text-sm"><Calendar className="h-4 w-4" strokeWidth={1.5} />{post.date}</div>
              <div className="flex items-center gap-2 font-body text-sm"><Clock className="h-4 w-4" strokeWidth={1.5} />{post.readTime}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="py-16 bg-white" data-testid="blog-detail-content">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2 mb-12 pb-8 border-b border-[#D5D1C8]">
            {post.tags.map((tag, idx) => (
              <span key={idx} className="font-body text-xs font-medium px-3 py-1 bg-[#C85A40]/10 text-[#C85A40]">#{tag}</span>
            ))}
          </div>
          
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-display prose-headings:text-[#111C16] prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-4
              prose-p:font-body prose-p:text-[#4A5D53] prose-p:text-base prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-[#C85A40] prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#111C16]
              prose-ul:my-6 prose-ul:space-y-2 
              prose-li:font-body prose-li:text-[#4A5D53] prose-li:text-base
              prose-ol:my-6 prose-ol:space-y-2"
            dangerouslySetInnerHTML={{ __html: (post.content || `<h2>Coming Soon</h2><p>This article is currently being prepared. Please check back soon.</p><p>${post.excerpt}</p>`).replace(/^\s+/gm, '').trim() }}
          />
          
          <div className="mt-16 pt-8 border-t border-[#D5D1C8]">
            <div className="flex items-center justify-between">
              <div>
                <p className="eyebrow text-[#4A5D53/60] mb-2">Share this article</p>
                <Button size="sm" variant="outline" className="border-[#1A3628] text-[#1A3628] hover:bg-[#1A3628] hover:text-white" data-testid="blog-share-btn">
                  <Share2 className="h-4 w-4 mr-2" strokeWidth={1.5} />Share
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* CTA */}
      {/* <section className="relative py-16 md:py-20 bg-[#1A3628] overflow-hidden" data-testid="blog-detail-cta">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 50% 50%, #2D5A43 0%, transparent 70%)' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tighter mb-6">Ready to Protect Your Brand?</h2>
          <p className="font-body text-lg text-white/50 mb-12 max-w-xl mx-auto leading-relaxed">
            Let our expert IP attorneys secure your competitive advantage and unlock growth opportunities.
          </p>
          <Button onClick={() => handleButtonClick(`Start Protection - Blog: ${post.title}`)} className="bg-[#C85A40] hover:bg-[#A64730] text-white btn-hover" data-testid="blog-cta-start-btn">Start Your Protection</Button>
        </div>
      </section> */}

      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} />
    </div>
  );
};

export default BlogDetail;
